/* @flow */

import React from 'react';
import Immutable from 'immutable';
import FontAwesome from 'react-fontawesome';
import PropTypes from '../util/prop_types';

const ANCHOR_POSITION = {
  top: {x: 0.5, y: 0},
  'top-left': {x: 0, y: 0},
  'top-right': {x: 1, y: 0},
  bottom: {x: 0.5, y: 1},
  'bottom-left': {x: 0, y: 1},
  'bottom-right': {x: 1, y: 1},
  left: {x: 0, y: 0.5},
  right: {x: 1, y: 0.5}
};

export default class Popup extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      positionX: 0,
      positionY: 0
    };
  }

  static propTypes = {
    longitude: PropTypes.number.isRequired,
    latitude: PropTypes.number.isRequired,
    offsetLeft: PropTypes.number,
    offsetTop: PropTypes.number,
    tipSize: PropTypes.number,
    closeButton: PropTypes.bool,
    closeOnClick: PropTypes.bool,
    // The popup's location relative to the coordinate
    anchor: PropTypes.oneOf(Object.keys(ANCHOR_POSITION)),
    // Whether the popup anchor should be auto-adjusted to fit within the container
    dynamicPosition: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.element.isRequired,
  }

  static defaultProps = {
    offsetLeft: 0,
    offsetTop: 0,
    tipSize: 10,
    anchor: 'bottom',
    dynamicPosition: true,
    closeButton: true,
    closeOnClick: false,
    onClose: () => {}
  }

  static contextTypes = {
    map: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
  };

  componentDidMount() {
    this.context.map.on('move', this.handleMapMove);

    // Now this.container is rendered and the size of container is known.
    // Recalculate the anchor/position
    this.handleMapMove();

    // Container just got a size, re-calculate position
    this.forceUpdate();
  }

  componentWillUnmount() {
    this.prevent = true;

    this.context.map.off('move', this.handleMapMove);
  }

  componentWillReceiveProps(nextProps) {
    const {longitude, latitude} = nextProps;
    if (!this.prevent) {
      const position = this.context.map.project([longitude, latitude]);
      this.setState({positionX: position.x, positionY: position.y});
    }
  }

  handleMapMove = () => {
    const {longitude, latitude} = this.props;
    if (!this.prevent) {
      const position = this.context.map.project([longitude, latitude]);
      this.setState({positionX: position.x, positionY: position.y});
    }
  };

  _getPosition = (x, y) => {
    const {map, width, height} = this.context;
    const {anchor, dynamicPosition, tipSize} = this.props;

    if (this._content) {
      if (dynamicPosition) {
        let padding = tipSize | 0;
        let selfWidth = this._content.clientWidth;
        let selfHeight = this._content.clientHeight;
        let {x: anchorX, y: anchorY} = ANCHOR_POSITION[anchor];

        // anchorY: top - 0, center - 0.5, bottom - 1
        let top = y - anchorY * selfHeight;
        let bottom = top + selfHeight;
        // If needed, adjust anchorY at 0.5 step between [0, 1]
        const yStep = 0.5;

        if (top < padding) {
          // Top edge is outside, try move down
          while (top < padding && anchorY >= yStep) {
            anchorY -= yStep;
            top += yStep * selfHeight;
          }
        } else if (bottom > height - padding) {
          // bottom edge is outside, try move up
          while (bottom > height - padding && anchorY <= 1 - yStep) {
            anchorY += yStep;
            bottom -= yStep * selfHeight;
          }
        }

        // anchorX: left - 0, center - 0.5, right - 1
        let left = x - anchorX * selfWidth;
        let right = left + selfWidth;

        // If needed, adjust anchorX at 0.5 step between [0, 1]
        let xStep = 0.5;
        if (anchorY === 0.5) {
          // If y is centered, then x cannot also be centered
          anchorX = Math.floor(anchorX);
          xStep = 1;
        }

        if (left < padding) {
          // Left edge is outside, try move right
          while (left < padding && anchorX >= xStep) {
            anchorX -= xStep;
            left += xStep * selfWidth;
          }
        } else if (right > width - padding) {
          // Right edge is outside, try move left
          while (right > width - padding && anchorX <= 1 - xStep) {
            anchorX += xStep;
            right -= xStep * selfWidth;
          }
        }

        // Find the name of the new anchor position
        return Object.keys(ANCHOR_POSITION).find((positionType) => {
          const anchorPosition = ANCHOR_POSITION[positionType];
          return anchorPosition.x === anchorX && anchorPosition.y === anchorY;
        });
      }
      else {
        return anchor;
      }
    }

    return anchor;
  }

  render(): ?React.Element<any> {
    const {offsetLeft, offsetTop, closeOnClick} = this.props;

    const x = this.state.positionX + offsetLeft;
    const y = this.state.positionY + offsetTop;

    const positionType = this._getPosition(x, y);
    const anchorPosition = ANCHOR_POSITION[positionType];

    const containerStyle = {
      position: 'absolute',
      left: x + offsetLeft,
      top: y + offsetTop,
      transform: `translate(${-anchorPosition.x * 100}%, ${-anchorPosition.y * 100}%)`
    };

    return (
      <div
        className={`mapboxgl-popup mapboxgl-popup-anchor-${positionType}`}
        style={containerStyle}
        onClick={closeOnClick ? this.props.onClose : null} >
        <div key="tip"
          className='mapboxgl-popup-tip'
          style={{borderWidth: this.props.tipSize}} />
        <div key='content'
          ref={(ref) => {this._content = ref;}}
          className='mapboxgl-popup-content' >
          {this.props.closeButton &&
            <button
              key='close-button'
              className='mapboxgl-popup-close-button'
              type='button'
              onClick={this.props.onClose} >
              <FontAwesome
                name="times"
                ariaLabel="Close Popup" />
            </button>
          }
          {this.props.children}
        </div>
      </div>
    );
  }
}
